import React, { useMemo, useState } from "react";
import { Checkbox, ConfigProvider, Input, Table, Tag, Typography, theme } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { formattedPlayerName } from "@utils/name_utils";
import { keyOf, useCellNote, useCellSelected, useSelectionVersion } from "../hooks/useBulkTagSelection";
import { useTableBodyHeight } from "../hooks/useTableBodyHeight";

const { Text } = Typography;

const PLAYER_COL_WIDTH = 240;
// A checkbox under the title "Flag".
const FLAG_COL_WIDTH = 56;

// Enter moves to the next checked row's note in screen order (after the search), so a coach can
// type a column of weigh-in numbers without the mouse.
const focusNextNote = (e) => {
    const inputs = [...e.currentTarget.closest(".stv2-bulk-day-table").querySelectorAll("[data-day-note]:not(:disabled)")];
    inputs[inputs.indexOf(e.currentTarget) + 1]?.focus();
};

// Unchecking a row also clears its note, so checking it again starts from an empty note.
const DayCheckbox = React.memo(({ store, cellKey }) => {
    const checked = useCellSelected(store, cellKey);
    const onChange = (e) => {
        store.setKeys([cellKey], e.target.checked);
        if (!e.target.checked) store.setNote(cellKey, "");
    };
    return <Checkbox checked={checked} onChange={onChange} />;
});

// Editable only while the row is checked.
const DayNote = React.memo(({ store, cellKey }) => {
    const checked = useCellSelected(store, cellKey);
    const note = useCellNote(store, cellKey);
    return (
        <Input
            data-day-note
            variant="borderless"
            size="small"
            value={note}
            disabled={!checked}
            onChange={(e) => store.setNote(cellKey, e.target.value)}
            onPressEnter={focusNextNote}
            placeholder="Optional note"
            maxLength={5000}
        />
    );
});

// The rows a save would change: tagged or re-tagged, plus untagged. Hidden when there are none.
export const UnsavedPill = ({ store, changesFor }) => {
    useSelectionVersion(store);
    const { writes, removes } = changesFor();
    const n = writes.length + removes.length;
    if (n === 0) return null;
    return (
        <Tag color="warning" className="stv2-bulk-day-unsaved">
            {n} unsaved {n === 1 ? "change" : "changes"}
        </Tag>
    );
};

// One row per player for one date: the tag checkbox and the player's own note. The search
// only hides rows; a hidden row keeps its state and is still saved.
export const SingleDayTable = ({ store, players, day, teamName, loading, disabled, changesFor }) => {
    const [search, setSearch] = useState("");
    const { token } = theme.useToken();
    const [areaRef, bodyHeight] = useTableBodyHeight();

    const rows = useMemo(() => {
        const query = search.trim().toLowerCase();
        return players
            .map((player) => ({
                key: player.player_id,
                player,
                name: formattedPlayerName(teamName, `${player.first_name} ${player.last_name}`),
                cellKey: keyOf(player.player_id, day),
            }))
            .filter((row) => `${row.name} #${row.player.number}`.toLowerCase().includes(query));
    }, [players, teamName, day, search]);

    const columns = useMemo(
        () => [
            {
                // Titles and cells sit in blocks of their own, as in multi day: a global
                // `.ant-table-cell` rule centers every cell with !important, which `align` cannot
                // override.
                title: <div className="stv2-bulk-player">Player</div>,
                key: "player",
                width: PLAYER_COL_WIDTH,
                render: (_, row) => (
                    <div className="stv2-bulk-player">
                        <span>
                            {row.name} <Text type="secondary">#{row.player.number}</Text>
                        </span>
                    </div>
                ),
            },
            {
                title: "Flag",
                key: "flag",
                width: FLAG_COL_WIDTH,
                align: "center",
                render: (_, row) => <DayCheckbox store={store} cellKey={row.cellKey} />,
            },
            {
                title: <div className="stv2-bulk-day-notes-title">Notes</div>,
                key: "notes",
                render: (_, row) => <DayNote store={store} cellKey={row.cellKey} />,
            },
        ],
        [store]
    );

    return (
        <>
            <div className="stv2-bulk-day-toolbar">
                <Input
                    allowClear
                    size="small"
                    prefix={<SearchOutlined />}
                    placeholder="Search players"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="stv2-bulk-day-search"
                />
                <UnsavedPill store={store} changesFor={changesFor} />
            </div>
            <div ref={areaRef} className="stv2-bulk-table-area">
                <ConfigProvider componentDisabled={disabled}>
                    <Table
                        className="stv2-bulk-table stv2-bulk-day-table"
                        // The checked-row tint is antd's selected-row color, so it follows the theme.
                        style={{ "--stv2-row-on": token.controlItemBgActive }}
                        columns={columns}
                        dataSource={rows}
                        rowKey="key"
                        size="small"
                        bordered
                        loading={loading}
                        pagination={false}
                        locale={search.trim() ? { emptyText: "No players match" } : undefined}
                        scroll={{ y: bodyHeight }}
                    />
                </ConfigProvider>
            </div>
        </>
    );
};
